// WbTicketLeftRail — Phase C10 Group 2.
//
// Left rail of the New Ticket page. Owns party selection (customer /
// vehicle / haulier / driver) and the two notes textareas (customer-
// facing + internal). Customer selection is the trigger that turns the
// pending shell into a real server-side ticket via context.createTicket();
// the other pickers are disabled until that POST lands.
//
// Hook into context (see WbTicketContext):
//   • Without `?from=`: pendingShell is set after the Start modal closes.
//     ticket is null. Picking a customer fires POST /wb/tickets with the
//     full shell + customer_enc_id.
//   • With `?from=enc_id`: context.ticket is hydrated; party fields are
//     populated from ticket.haulier / vehicle / driver / notes. Picks
//     issue PATCHes (debounced for notes; immediate for party fields).
//
// Customer-settings flow:
//   • On every customer pick we fetch GET /wb/customer-settings/:enc_id
//     (60s cache). Defaults (default_haulier / default_vehicle) auto-
//     populate the matching pickers ONCE per ticket — guarded by a ref —
//     and only when the ticket has no current value (we never overwrite
//     an operator-set selection).
//   • account_status drives a Badge next to the customer name: 'on_hold'
//     → amber "On hold"; 'closed' → red "Account closed"; 'active' → no
//     badge.
//
// Vehicle stored-tare hint:
//   • Mirrors the tare_status flag on WbVehicle. 'fresh' → green text;
//     'stale' → amber "will weigh" warning; 'not_set' → muted note.

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { ScanLine } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../../api/client';
import { endpoints } from '../../../../api/endpoints';
import { queryKeys } from '../../../../api/queryKeys';
import { useWbLiveBridges } from '../../../../hooks/wb/useWbLiveBridges';
import { Badge } from '../../../../components/ui/Badge/Badge';
import { extractErrorMessage } from '../../../../utils/errorMessage';
import { useToastStore } from '../../../../store/toastStore';
import { WbCustomerSearch } from '../../components/WbCustomerSearch';
import { useVehicleRecognition } from '../../../../hooks/useVehicleRecognition';
import { VehicleRecognitionPopup } from '../../../../components/wb/VehicleRecognitionPopup';
import { useWbTicket } from '../../context/WbTicketContext';
import { useWbDrivers } from '../../hooks/useWbDrivers';
import { useWbHauliers } from '../../hooks/useWbHauliers';
import { useWbVehicles } from '../../hooks/useWbVehicles';
import type { CreateTicketInput } from '../../context/WbTicketContext';
import styles from './WbTicketLeftRail.module.css';

// -- Customer-settings DTO -------------------------------------------------
//
// Mirrors the wb_customer_settings shape the server returns via
// GET /wb/customer-settings/:customer_enc_id (see
// WEIGHBRIDGE_SERVER_CONTRACT.md). We only care about the four fields the
// left rail consumes — price_band isn't surfaced here (the centre pane's
// line-item resolver re-fetches it through POST /wb/pricing/resolve).

type AccountStatus = 'active' | 'on_hold' | 'closed';

interface WbCustomerSettingsRef {
  readonly enc_id: string;
  readonly name: string;
}

interface WbCustomerSettingsVehicleRef {
  readonly enc_id: string;
  readonly registration: string;
}

interface WbCustomerSettings {
  readonly customer_enc_id: string;
  readonly price_band: WbCustomerSettingsRef | null;
  readonly credit_limit: number | null;
  readonly account_status: AccountStatus;
  readonly default_haulier: WbCustomerSettingsRef | null;
  readonly default_vehicle: WbCustomerSettingsVehicleRef | null;
}

interface CustomerSettingsResponse {
  customer_settings: WbCustomerSettings;
}

const KG_FORMATTER = new Intl.NumberFormat('en-GB', {
  maximumFractionDigits: 0,
});

export function WbTicketLeftRail(): JSX.Element {
  const { ticket, pendingShell, createTicket, patchTicket, tareAutoFilled, setTareAutoFilled } = useWbTicket();
  const pushToast = useToastStore((s) => s.push);

  // -- Local control values ------------------------------------------------
  //
  // We mirror context.ticket into local state so the controls stay snappy
  // and don't await every render of the merged ticket. The notes inputs
  // hold their typing state locally; we pump it back into the context via
  // patchTicket() onChange (the context handles the debounced PATCH).
  // Customer is part of the ticket — read it straight from context (single
  // source of truth). The old local-state mirror reset to null on re-render /
  // remount, which forced the operator to re-select the customer every time.
  // The picker writes via createTicket / patchTicket, which flow back through
  // context, so there is nothing to mirror.
  const customerEncId = ticket?.customer?.enc_id ?? null;
  const [haulierEncId, setHaulierEncId] = useState<string | null>(
    ticket?.haulier?.enc_id ?? null,
  );
  const [vehicleEncId, setVehicleEncId] = useState<string | null>(
    ticket?.vehicle?.enc_id ?? null,
  );
  const [driverEncId, setDriverEncId] = useState<string | null>(
    ticket?.driver?.enc_id ?? null,
  );
  const [notes, setNotes] = useState<string>(ticket?.notes ?? '');
  const [internalNotes, setInternalNotes] = useState<string>(
    ticket?.internal_notes ?? '',
  );

  // Filter strings for the three search-and-select pickers. We follow the
  // shared WbCustomerSearch pattern: render an <input type="search"> on
  // top of a <select> and filter client-side on substring match.
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [haulierFilter, setHaulierFilter] = useState('');
  const [driverFilter, setDriverFilter] = useState('');

  // -- Sync local state ←→ context ticket ---------------------------------
  //
  // When the context's ticket shifts (createTicket lands, server patches
  // echo back, or hydrateFromTicket fires from a `?from=` flow), reset our
  // controlled values to the canonical version. We deliberately use
  // ticket.* enc_ids as the dependency — string identity is enough.
  const ticketEncId = ticket?.enc_id ?? null;
  useEffect(() => {
    if (ticket === null) return;
    setHaulierEncId(ticket.haulier?.enc_id ?? null);
    setVehicleEncId(ticket.vehicle?.enc_id ?? null);
    setDriverEncId(ticket.driver?.enc_id ?? null);
    // Notes are sticky to local state once the user is typing — only sync
    // them when we're hydrating a new ticket id (e.g. ?from= flow).
    setNotes(ticket.notes ?? '');
    setInternalNotes(ticket.internal_notes ?? '');
    setTareAutoFilled(ticket.tare_method === 'stored' && ticket.tare_weight_kg !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketEncId]);

  // -- Hauliers / vehicles / drivers (lists for the pickers) ---------------
  const hauliersQ = useWbHauliers({ is_active: true, per_page: 100 });
  const vehiclesQ = useWbVehicles({ is_active: true, per_page: 100 });
  const driversQ = useWbDrivers({ is_active: true, per_page: 100 });

  const hauliers = hauliersQ.data?.hauliers ?? [];
  const vehicles = vehiclesQ.data?.vehicles ?? [];
  const drivers = driversQ.data?.drivers ?? [];

  // -- Customer settings (account status, defaults) ------------------------
  const settingsQ = useQuery<CustomerSettingsResponse>({
    queryKey: queryKeys.wb.customerSettings(customerEncId ?? ''),
    queryFn: async () => {
      if (customerEncId === null) {
        throw new Error('customer-settings queryFn ran without a customer id');
      }
      const res = await apiClient.get<CustomerSettingsResponse>(
        endpoints.wb.customerSettings(customerEncId),
      );
      return res.data;
    },
    enabled: customerEncId !== null,
    staleTime: 60_000,
    // A failed settings fetch (e.g. a 401 from an expired session) must not
    // hammer the endpoint or block ticket creation — fail fast and let the
    // rail fall back to the customer name with a muted note (see render).
    retry: false,
  });

  const customerSettings = settingsQ.data?.customer_settings ?? null;

  // -- Auto-populate haulier / vehicle from customer-settings --------------
  //
  // Once a ticket exists AND customer-settings has loaded, fire a single
  // patchTicket() to set defaults — but ONLY for fields that are still
  // null. We guard with a ref keyed on the ticket+customer pair so the
  // effect doesn't loop after the optimistic merge bounces back.
  const defaultsAppliedRef = useRef<string | null>(null);
  useEffect(() => {
    if (ticket === null) return;
    if (customerSettings === null) return;
    if (customerSettings.customer_enc_id !== ticket.customer?.enc_id) return;
    const guardKey = `${ticket.enc_id}::${customerSettings.customer_enc_id}`;
    if (defaultsAppliedRef.current === guardKey) return;
    defaultsAppliedRef.current = guardKey;

    const patch: {
      haulier_enc_id?: string;
      vehicle_enc_id?: string;
    } = {};
    if (
      ticket.haulier === null &&
      customerSettings.default_haulier !== null
    ) {
      patch.haulier_enc_id = customerSettings.default_haulier.enc_id;
    }
    if (
      ticket.vehicle === null &&
      customerSettings.default_vehicle !== null
    ) {
      patch.vehicle_enc_id = customerSettings.default_vehicle.enc_id;
    }
    if (Object.keys(patch).length > 0) {
      patchTicket(patch);
    }
  }, [ticket, customerSettings, patchTicket]);

  // -- Customer pick handler ----------------------------------------------
  //
  // If no ticket yet: build a CreateTicketInput from pendingShell +
  // customer_enc_id and POST. If a ticket exists: PATCH the customer.
  // Either way we update local state immediately so the picker stays
  // responsive while the network call settles.
  async function handleCustomerChange(
    nextEncId: string | null,
  ): Promise<void> {
    if (nextEncId === null) {
      // Don't allow clearing customer once a ticket exists (server requires
      // it). Picker UX matches: an empty selection is treated as "no change".
      return;
    }
    if (ticket === null) {
      if (pendingShell === null) {
        // Shouldn't happen — page guards mounting this rail until a
        // pendingShell or ticket exists. Swallow defensively.
        pushToast({
          type: 'error',
          message: 'Cannot create ticket — missing direction or yard.',
        });
        return;
      }
      const input: CreateTicketInput = {
        direction: pendingShell.direction,
        weighing_mode: pendingShell.weighing_mode,
        yard_enc_id: pendingShell.yard_enc_id,
        customer_enc_id: nextEncId,
      };
      if (pendingShell.weighbridge_enc_id !== undefined) {
        input.weighbridge_enc_id = pendingShell.weighbridge_enc_id;
      }
      try {
        await createTicket(input);
      } catch (err) {
        pushToast({
          type: 'error',
          message: extractErrorMessage(err),
        });
      }
      return;
    }
    // Ticket exists — PATCH the new customer in.
    if (nextEncId !== ticket.customer?.enc_id) {
      // Reset the defaults guard so the new customer's defaults can
      // populate the still-empty fields once /customer-settings/:newId
      // resolves.
      defaultsAppliedRef.current = null;
      patchTicket({ customer_enc_id: nextEncId });
    }
  }

  // -- Vehicle filter (client-side substring on registration) -------------
  const filteredVehicles = useMemo(() => {
    const q = vehicleFilter.trim().toLowerCase();
    if (q === '') return vehicles.slice(0, 200);
    return vehicles
      .filter((v) => v.registration.toLowerCase().includes(q))
      .slice(0, 200);
  }, [vehicles, vehicleFilter]);

  // -- Haulier filter ------------------------------------------------------
  const filteredHauliers = useMemo(() => {
    const q = haulierFilter.trim().toLowerCase();
    if (q === '') return hauliers.slice(0, 200);
    return hauliers
      .filter((h) => h.name.toLowerCase().includes(q))
      .slice(0, 200);
  }, [hauliers, haulierFilter]);

  // -- Driver filter -------------------------------------------------------
  const filteredDrivers = useMemo(() => {
    const q = driverFilter.trim().toLowerCase();
    if (q === '') return drivers.slice(0, 200);
    return drivers
      .filter((d) => d.name.toLowerCase().includes(q))
      .slice(0, 200);
  }, [drivers, driverFilter]);

  // Vehicle recognition popup (Phase 2 — USB barcode scanner).
  const { event: vehicleRecognizedEvent, dismiss: dismissVehiclePopup } =
    useVehicleRecognition(ticket !== null);

  // -- ANPR "Scan Plate" button -------------------------------------------
  const [isScanning, setIsScanning] = useState(false);
  const bridgesQ = useWbLiveBridges();
  const activeBridgeEncId =
    ticket?.weighbridge?.enc_id ?? pendingShell?.weighbridge_enc_id ?? null;
  const activeBridge = useMemo(
    () =>
      activeBridgeEncId !== null
        ? (bridgesQ.data?.bridges ?? []).find((b) => b.enc_id === activeBridgeEncId) ?? null
        : null,
    [activeBridgeEncId, bridgesQ.data?.bridges],
  );
  const anprConfigured = activeBridge?.anpr_configured ?? false;
  const activeBridgeNumericId = activeBridge?.weighbridge_id ?? null;

  async function handleScanPlate(): Promise<void> {
    if (activeBridgeNumericId === null) return;
    setIsScanning(true);
    try {
      await apiClient.post(endpoints.wb.triggerAnpr, {
        weighbridge_id: activeBridgeNumericId,
      });
      // Result arrives via SSE vehicle_recognized → popup auto-opens.
      // Auto-clear scanning state after 5s if no SSE arrives.
      setTimeout(() => setIsScanning(false), 5000);
    } catch {
      setIsScanning(false);
    }
  }

  // -- Shared vehicle selection logic (used by the select control and the
  //    recognition popup so both paths apply the same tare auto-fill). ------
  function applyVehicleByEncId(encId: string | null): void {
    if (ticket === null) return;
    setVehicleEncId(encId);
    const patch: Parameters<typeof patchTicket>[0] = { vehicle_enc_id: encId };
    if (encId !== null) {
      const selected = vehicles.find((veh) => veh.enc_id === encId) ?? null;
      if (selected?.stored_tare_kg != null && selected.tare_status === 'fresh') {
        patch.tare_weight_kg = selected.stored_tare_kg;
        setTareAutoFilled(true);
      } else {
        setTareAutoFilled(false);
      }
    } else {
      setTareAutoFilled(false);
    }
    patchTicket(patch);
  }

  // -- Vehicle select handler ---------------------------------------------
  function handleVehicleChange(
    e: ChangeEvent<HTMLSelectElement>,
  ): void {
    const v = e.target.value;
    applyVehicleByEncId(v === '' ? null : v);
  }

  function handleHaulierChange(
    e: ChangeEvent<HTMLSelectElement>,
  ): void {
    if (ticket === null) return;
    const v = e.target.value;
    const next = v === '' ? null : v;
    setHaulierEncId(next);
    patchTicket({ haulier_enc_id: next });
  }

  function handleDriverChange(
    e: ChangeEvent<HTMLSelectElement>,
  ): void {
    if (ticket === null) return;
    const v = e.target.value;
    const next = v === '' ? null : v;
    setDriverEncId(next);
    patchTicket({ driver_enc_id: next });
  }

  function handleNotesChange(e: ChangeEvent<HTMLTextAreaElement>): void {
    const v = e.target.value;
    setNotes(v);
    if (ticket !== null) {
      patchTicket({ notes: v });
    }
  }

  function handleInternalNotesChange(
    e: ChangeEvent<HTMLTextAreaElement>,
  ): void {
    const v = e.target.value;
    setInternalNotes(v);
    if (ticket !== null) {
      patchTicket({ internal_notes: v });
    }
  }

  // -- Derived: selected vehicle (for the tare hint) ----------------------
  //
  // We resolve against the loaded list first; if the ticket was hydrated
  // before the list landed we fall back to the ticket's own vehicle ref
  // (the tare hint is suppressed in that case — we don't have tare_status
  // on the TicketVehicleRef, just the registration).
  const selectedVehicle = useMemo(() => {
    if (vehicleEncId === null) return null;
    return vehicles.find((v) => v.enc_id === vehicleEncId) ?? null;
  }, [vehicles, vehicleEncId]);

  // -- Account status badge -----------------------------------------------
  const accountStatus: AccountStatus | null =
    customerSettings?.account_status ?? null;

  // -- Disabled state for non-customer pickers ----------------------------
  //
  // The brief allows the simpler UX: vehicle/haulier/driver/notes are
  // disabled until a ticket exists. The customer pick is what flips that
  // switch (via createTicket()).
  const partyControlsDisabled = ticket === null;

  return (
    <div className={styles.rail} data-testid="wb-left-rail">
      {/* -- Customer section ----------------------------------------------- */}
      <section className={styles.section} data-testid="wb-left-rail-customer">
        <h3 className={styles.sectionHeading}>
          Customer<span className={styles.required}>*</span>
        </h3>
        <WbCustomerSearch
          value={customerEncId}
          // The ticket DTO's customer enc_id is a different ciphertext than the
          // /customers list (random-IV re-encryption per response), so pass the
          // ticket's customer name to keep the selection visible even when no
          // list option matches the value.
          selectedName={ticket?.customer?.name ?? null}
          onChange={(encId) => {
            // The customer picker returns null when the operator clears
            // the select — we ignore that case (see handleCustomerChange).
            void handleCustomerChange(encId);
          }}
          ariaLabel="Customer"
        />
        {accountStatus !== null && accountStatus !== 'active' ? (
          <div className={styles.row}>
            {accountStatus === 'on_hold' ? (
              <Badge variant="warning">On hold</Badge>
            ) : (
              <Badge variant="danger">Account closed</Badge>
            )}
          </div>
        ) : null}
        {customerSettings?.credit_limit !== undefined &&
        customerSettings?.credit_limit !== null ? (
          <span className={styles.creditLimit}>
            Credit limit: £{customerSettings.credit_limit}
          </span>
        ) : null}
        {customerEncId !== null && settingsQ.isError ? (
          <span
            className={styles.helperLocked}
            data-testid="wb-left-rail-settings-error"
          >
            Account settings unavailable — you can still create the ticket.
          </span>
        ) : null}
      </section>

      {partyControlsDisabled ? (
        <span className={styles.helperLocked}>
          Pick a customer to enable these fields.
        </span>
      ) : null}

      {/* -- Vehicle section ----------------------------------------------- */}
      <section className={styles.section} data-testid="wb-left-rail-vehicle">
        <label className={styles.sectionLabel} htmlFor="wb-left-rail-vehicle-select">
          Vehicle
        </label>
        <input
          type="search"
          className={styles.input}
          placeholder="Filter by registration…"
          value={vehicleFilter}
          onChange={(e) => setVehicleFilter(e.target.value)}
          disabled={partyControlsDisabled || vehiclesQ.isLoading}
          aria-label="Filter vehicle list"
        />
        <select
          id="wb-left-rail-vehicle-select"
          className={styles.select}
          value={vehicleEncId ?? ''}
          onChange={handleVehicleChange}
          disabled={partyControlsDisabled || vehiclesQ.isLoading}
          aria-label="Vehicle"
          data-testid="wb-left-rail-vehicle-select"
        >
          <option value="">— No vehicle —</option>
          {filteredVehicles.map((v) => (
            <option key={v.enc_id} value={v.enc_id}>
              {v.registration}
            </option>
          ))}
        </select>
        {selectedVehicle !== null ? (
          <span
            className={
              selectedVehicle.tare_status === 'fresh'
                ? `${styles.tareInfo} ${styles.tareFresh}`
                : selectedVehicle.tare_status === 'stale'
                  ? `${styles.tareInfo} ${styles.tareStale}`
                  : `${styles.tareInfo} ${styles.tareNotSet}`
            }
            data-testid="wb-left-rail-vehicle-tare"
          >
            {selectedVehicle.tare_status === 'fresh' &&
            selectedVehicle.stored_tare_kg !== null
              ? `Stored tare: ${KG_FORMATTER.format(selectedVehicle.stored_tare_kg)} kg`
              : selectedVehicle.tare_status === 'stale' &&
                  selectedVehicle.stored_tare_kg !== null
                ? `Stale tare: ${KG_FORMATTER.format(selectedVehicle.stored_tare_kg)} kg — will weigh`
                : 'No stored tare'}
          </span>
        ) : null}
        {tareAutoFilled ? (
          <span
            className={styles.tareAutoFilled}
            data-testid="wb-tare-autofilled-hint"
          >
            Loaded from vehicle record
          </span>
        ) : null}
        <button
          type="button"
          className={styles.scanPlateBtn}
          onClick={() => { void handleScanPlate(); }}
          disabled={!anprConfigured || isScanning || partyControlsDisabled}
          title={
            anprConfigured
              ? 'Scan number plate with ANPR camera'
              : 'ANPR camera not configured for this bridge'
          }
          data-testid="wb-scan-plate-btn"
        >
          <ScanLine size={14} />
          {isScanning ? 'Scanning…' : 'Scan Plate'}
        </button>
      </section>

      {/* -- Haulier section ----------------------------------------------- */}
      <section className={styles.section} data-testid="wb-left-rail-haulier">
        <label className={styles.sectionLabel} htmlFor="wb-left-rail-haulier-select">
          Haulier
        </label>
        <input
          type="search"
          className={styles.input}
          placeholder="Filter haulier list…"
          value={haulierFilter}
          onChange={(e) => setHaulierFilter(e.target.value)}
          disabled={partyControlsDisabled || hauliersQ.isLoading}
          aria-label="Filter haulier list"
        />
        <select
          id="wb-left-rail-haulier-select"
          className={styles.select}
          value={haulierEncId ?? ''}
          onChange={handleHaulierChange}
          disabled={partyControlsDisabled || hauliersQ.isLoading}
          aria-label="Haulier"
          data-testid="wb-left-rail-haulier-select"
        >
          <option value="">— No haulier —</option>
          {filteredHauliers.map((h) => (
            <option key={h.enc_id} value={h.enc_id}>
              {h.name}
            </option>
          ))}
        </select>
      </section>

      {/* -- Driver section ------------------------------------------------ */}
      <section className={styles.section} data-testid="wb-left-rail-driver">
        <label className={styles.sectionLabel} htmlFor="wb-left-rail-driver-select">
          Driver
        </label>
        <input
          type="search"
          className={styles.input}
          placeholder="Filter driver list…"
          value={driverFilter}
          onChange={(e) => setDriverFilter(e.target.value)}
          disabled={partyControlsDisabled || driversQ.isLoading}
          aria-label="Filter driver list"
        />
        <select
          id="wb-left-rail-driver-select"
          className={styles.select}
          value={driverEncId ?? ''}
          onChange={handleDriverChange}
          disabled={partyControlsDisabled || driversQ.isLoading}
          aria-label="Driver"
          data-testid="wb-left-rail-driver-select"
        >
          <option value="">— No driver —</option>
          {filteredDrivers.map((d) => (
            <option key={d.enc_id} value={d.enc_id}>
              {d.name}
            </option>
          ))}
        </select>
      </section>

      {/* -- Notes section ------------------------------------------------- */}
      <section className={styles.section} data-testid="wb-left-rail-notes">
        <label className={styles.sectionLabel} htmlFor="wb-left-rail-notes-input">
          Notes (visible to customer)
        </label>
        <textarea
          id="wb-left-rail-notes-input"
          className={styles.textarea}
          value={notes}
          maxLength={5000}
          onChange={handleNotesChange}
          disabled={partyControlsDisabled}
          placeholder="Notes shown on the printed ticket / receipt…"
          data-testid="wb-left-rail-notes-input"
        />
      </section>

      <section className={styles.section} data-testid="wb-left-rail-internal-notes">
        <label
          className={styles.sectionLabel}
          htmlFor="wb-left-rail-internal-notes-input"
        >
          Internal notes (operator-only)
        </label>
        <textarea
          id="wb-left-rail-internal-notes-input"
          className={styles.textarea}
          value={internalNotes}
          maxLength={5000}
          onChange={handleInternalNotesChange}
          disabled={partyControlsDisabled}
          placeholder="Notes kept internal — not printed."
          data-testid="wb-left-rail-internal-notes-input"
        />
      </section>

      {vehicleRecognizedEvent && (
        <VehicleRecognitionPopup
          key={vehicleRecognizedEvent.registration}
          event={vehicleRecognizedEvent}
          onDismiss={dismissVehiclePopup}
          onUseVehicle={(v) => {
            applyVehicleByEncId(v.enc_id);
            dismissVehiclePopup();
          }}
        />
      )}
    </div>
  );
}
